import React from "react";
import { Leaf, MapPin, Mail, Phone } from "lucide-react";

const Contact = ({ setProduct }) => {
  // Handler to send data to WhatsApp
  const handleSubmit = (e) => {
    e.preventDefault();

    // 1. Get data directly from the form
    const form = e.target;
    const name = form.fullName.value;
    const email = form.email.value;
    const interest = form.interest.value;
    const details = form.details.value;

    // 2. Format the message
    const message = `*Inquiry*
----------------
*Name:* ${name}
*Email:* ${email}
*Product Interest:* ${interest}
*Message:* ${details}`;

    const phoneNumber = "919963325524";

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <section
      id="contact"
      className="py-16 md:py-24 bg-primary text-white scroll-mt-16 relative overflow-hidden"
    >
      {/* Background Decorative Leaf */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-5">
        <Leaf className="absolute -top-20 -left-20 text-accent w-64 h-64 md:w-96 md:h-96" />
      </div>

      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Contact Information Column */}
          <div className="space-y-6 md:space-y-8 text-center lg:text-left">
            <div>
              <h2 className="text-3xl md:text-5xl font-black text-accent uppercase tracking-tighter mb-4">
                Connect With Us
              </h2>
              <p className="text-white/80 text-base md:text-lg leading-relaxed">
                Ready to bring premium Indian flavours to your market? Get in
                touch for a customized quote.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-1 gap-6">
              {/* Location */}
              <div className="flex items-center gap-4 md:gap-5 justify-start">
                <div className="h-12 w-12 md:h-14 md:w-14 rounded-full bg-white/10 flex items-center justify-center text-accent shadow-lg border border-white/5 shrink-0">
                  <MapPin size={24} />
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-accent uppercase tracking-wider text-xs mb-1">
                    Head Office
                  </h4>
                  <p className="text-white/80 text-sm md:text-base">
                    Hyderabad, Telangana, India
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-4 md:gap-5 justify-start">
                <div className="h-12 w-12 md:h-14 md:w-14 rounded-full bg-white/10 flex items-center justify-center text-accent shadow-lg border border-white/5 shrink-0">
                  <Mail size={24} />
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-accent uppercase tracking-wider text-xs mb-1">
                    Email Us
                  </h4>
                  <a
                    href="mailto:abhyuday@indoglobusexim.com"
                    className="text-white/80 text-sm md:text-base hover:text-accent transition-colors"
                  >
                    abhyuday@indoglobusexim.com
                  </a>
                  <p>
                    <a
                      href="mailto:chandrakanth@indoglobusexim.com"
                      className="text-white/80 text-sm md:text-base hover:text-accent transition-colors"
                    >
                      chandrakanth@indoglobusexim.com
                    </a>
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-4 md:gap-5 justify-start">
                <div className="h-12 w-12 md:h-14 md:w-14 rounded-full bg-white/10 flex items-center justify-center text-accent shadow-lg border border-white/5 shrink-0">
                  <Phone size={24} />
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-accent uppercase tracking-wider text-xs mb-1">
                    Call Us
                  </h4>
                  <div className="flex flex-col">
                    <a
                      href="tel:+919963325524"
                      className="text-white/80 text-sm md:text-base hover:text-accent transition-colors"
                    >
                      +91 9963325524
                    </a>
                    <a
                      href="tel:+919133882606"
                      className="text-white/80 text-sm md:text-base hover:text-accent transition-colors"
                    >
                      +91 9133882606
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Inquiry Form Column */}
          <div className="bg-white p-6 md:p-12 rounded-2xl md:rounded-3xl shadow-2xl text-slate-900 relative">
            <h3 className="text-2xl md:text-3xl font-black mb-6 md:mb-8 text-primary uppercase tracking-tighter">
              Let's have a conversation
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                <input
                  name="fullName"
                  type="text"
                  placeholder="Full Name"
                  required
                  className="w-full bg-white border-2 border-slate-200 p-3 md:p-4 rounded-xl outline-none focus:border-accent transition-all text-sm md:text-base font-medium"
                />
                <input
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  required
                  className="w-full bg-white border-2 border-slate-200 p-3 md:p-4 rounded-xl outline-none focus:border-accent transition-all text-sm md:text-base font-medium"
                />
              </div>
              <select
                name="interest"
                onChange={(e) => setProduct(e.target.value)}
                className="w-full bg-white border-2 border-slate-200 p-3 md:p-4 rounded-xl outline-none focus:border-accent text-slate-600 text-sm md:text-base appearance-none font-medium"
              >
                <option value="General Inquiry">Select Product Interest</option>
                <option value="Moringa Powder">Moringa Powder</option>
                <option value="Onion Powder">Onion Powder</option>
                <option value="Garlic Powder">Garlic Powder</option>
                <option value="Beetroot Powder">Beetroot Powder</option>
                <option value="Tomato Powder">Tomato Powder</option>
                <option value="Bulk Grocery Orders">Bulk Grocery Orders</option>
              </select>
              <textarea
                name="details"
                placeholder="Tell us about your requirements..."
                rows="3"
                className="w-full bg-white border-2 border-slate-200 p-3 md:p-4 rounded-xl outline-none focus:border-accent transition-all text-sm md:text-base resize-none font-medium"
              ></textarea>
              <button
                type="submit"
                className="w-full bg-accent text-primary font-black py-4 md:py-5 rounded-xl hover:bg-black hover:text-white transition-all shadow-lg uppercase tracking-widest text-base md:text-lg"
              >
                Send Inquiry via WhatsApp
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
